"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { cn } from "../lib/utils"
import { vehicleService, VEHICLE_SEPARATOR, type VehicleMake, type VehicleModel, type VehicleType } from "../services/vehicleService"
import { useVehicle } from "../contexts/VehicleContext";
import { useAuth } from "../contexts/AuthContext";
import { X } from "./icons";

// Mock UI components
const Dialog = ({ children, open, onOpenChange, ...props }: any) => {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" {...props}>
      <div className="bg-zinc-900 rounded-lg shadow-lg max-w-md w-full">
        {children}
        <div className="absolute top-0 right-0 p-4">
          <button onClick={() => onOpenChange(false)} className="text-zinc-400 hover:text-white">
            <X />
          </button>
        </div>
      </div>
    </div>
  )
}

const DialogContent = ({ children, className, ...props }: any) => (
  <div className={cn("p-6", className)} {...props}>
    {children}
  </div>
)

const DialogHeader = ({ children, ...props }: any) => (
  <div className="mb-4" {...props}>
    {children}
  </div>
)

const DialogTitle = ({ children, className, ...props }: any) => (
  <h2 className={cn("text-xl font-bold", className)} {...props}>
    {children}
  </h2>
)

const DialogFooter = ({ children, ...props }: any) => (
  <div className="mt-6 flex justify-end gap-2" {...props}>
    {children}
  </div>
)

const Label = ({ children, htmlFor, className, ...props }: any) => (
  <label htmlFor={htmlFor} className={cn("text-sm font-medium block mb-1", className)} {...props}>
    {children}
  </label>
)

const Input = ({ id, value, onChange, placeholder, className, autoFocus, ...props }: any) => (
  <input
    id={id}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    autoFocus={autoFocus}
    className={cn("w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white", className)}
    {...props}
  />
)

const Button = ({ children, className, onClick, type, variant, disabled, ...props }: any) => (
  <button
    className={cn(
      "px-4 py-2 rounded font-medium focus:outline-none",
      variant === "outline" && "border border-zinc-700 bg-transparent",
      disabled && "opacity-50 cursor-not-allowed",
      className,
    )}
    onClick={onClick}
    type={type}
    disabled={disabled}
    {...props}
  >
    {children}
  </button>
)

interface AddVehicleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddVehicleDialog({ open, onOpenChange }: AddVehicleDialogProps) {
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [makes, setMakes] = useState<VehicleMake[]>([]);
  const [models, setModels] = useState<VehicleModel[]>([]);
  const [types, setTypes] = useState<VehicleType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addVehicle } = useVehicle();
  const { getFreshToken } = useAuth();

  // Load vehicle makes when dialog opens
  useEffect(() => {
    if (open) {
      const loadMakes = async () => {
        try {
          setIsLoading(true);
          const freshToken = await getFreshToken();
          const vehicleMakes = await vehicleService.getAllVehicleMakes(freshToken || undefined);
          setMakes(vehicleMakes);
        } catch (error) {
          console.error("Error loading vehicle makes:", error);
          setMakes([]);
        } finally {
          setIsLoading(false);
        }
      };
      loadMakes();
    }
  }, [open, getFreshToken]);

  // Reset form when dialog closes (cleanup)
  useEffect(() => {
    if (!open) {
      setSelectedMake("");
      setSelectedModel("");
      setSelectedType("");
      setMakes([]);
      setModels([]);
      setTypes([]);
    }
  }, [open]);

  // Load models when make changes
  useEffect(() => {
    if (selectedMake) {
      const loadModels = async () => {
        try {
          const freshToken = await getFreshToken();
          const vehicleModels = await vehicleService.getVehicleModelsByMake(selectedMake, freshToken || undefined);
          setModels(vehicleModels);
          
          // Auto-select if only one model available
          if (vehicleModels.length === 1) {
            setSelectedModel(vehicleModels[0].id);
          }
        } catch (error) {
          console.error("Error loading vehicle models:", error);
          setModels([]);
        }
      };
      loadModels();
    } else {
      setModels([]);
    }
  }, [selectedMake, getFreshToken]);

  // Load types when model changes
  useEffect(() => {
    if (selectedMake && selectedModel) {
      const loadTypes = async () => {
        try {
          const freshToken = await getFreshToken();
          const vehicleTypes = await vehicleService.getVehicleTypesByModel(selectedMake, selectedModel, freshToken || undefined);
          setTypes(vehicleTypes);
          
          // Auto-select if only one type available
          if (vehicleTypes.length === 1) {
            setSelectedType(vehicleTypes[0].id);
          }
        } catch (error) {
          console.error("Error loading vehicle types:", error);
          setTypes([]);
        }
      };
      loadTypes();
    } else {
      setTypes([]);
    }
  }, [selectedMake, selectedModel, getFreshToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedMake && selectedModel && selectedType) {
      try {
        const vehicleId = `${selectedMake}${VEHICLE_SEPARATOR}${selectedModel}${VEHICLE_SEPARATOR}${selectedType}`;
        
        let vehicleName = "";
        try {
          const freshToken = await getFreshToken();
          vehicleName = await vehicleService.getVehicleNameById(vehicleId, freshToken || undefined);
        } catch (error) {
          console.warn("Could not fetch vehicle name from service, generating from selection:", error);
          // Fallback: generate name from the selected make/model/type data
          const makeData = makes.find(m => m.id === selectedMake);
          const modelData = models.find(m => m.id === selectedModel);
          const typeData = types.find(t => t.id === selectedType);
          vehicleName = `${makeData?.name || selectedMake} ${modelData?.name || selectedModel} ${typeData?.name || selectedType}`;
        }
        
        const vehicle = {
          id: vehicleId,
          name: vehicleName,
          make: selectedMake,
          model: selectedModel,
          type: selectedType,
        };
        
        await addVehicle(vehicle); // Add the vehicle to the context
        
        // Close dialog after vehicle is added - form will be reset by useEffect
        onOpenChange(false);
      } catch (error) {
        console.error("Error adding vehicle:", error);
        // Still close the dialog even if there was an error, 
        // so user doesn't get stuck
        onOpenChange(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-zinc-900 text-white border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-xl">Add New Vehicle</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="make">Make</Label>
              <select
                id="make"
                value={selectedMake}
                onChange={(e) => {
                  setSelectedMake(e.target.value);
                  setSelectedModel("");
                  setSelectedType("");
                }}
                className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white"
                disabled={isLoading}
              >
                <option value="">{isLoading ? "Loading..." : "Select Make"}</option>
                {makes.map((make) => (
                  <option key={make.id} value={make.id}>
                    {make.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <select
                id="model"
                value={selectedModel}
                onChange={(e) => {
                  setSelectedModel(e.target.value);
                  setSelectedType("");
                }}
                className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white"
                disabled={!selectedMake}
              >
                <option value="">Select Model</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded p-2 text-white"
                disabled={!selectedModel}
              >
                <option value="">Select Type</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.years})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedMake || !selectedModel || !selectedType}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Add Vehicle
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
